import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "@/components/ui/accordion";
import {
  LogIn, LayoutDashboard, ClipboardList, Users, Package,
  BarChart3, UserCog, Plus, ArrowDownCircle, ArrowUpCircle, Upload,
  Eye, Pencil, Trash2, History, ChevronRight, AlertTriangle, FileText,
  Search, Download, Settings, Filter, Maximize2, Printer, CheckCircle2, RotateCcw,
  Truck, Building2, BoxIcon, RefreshCw, CalendarIcon, HardHat, Sparkles
} from "lucide-react";

const Section = ({ icon: Icon, title, badge, children }) => (
  <Card className="border border-border shadow-none">
    <CardHeader className="pb-3">
      <CardTitle className="font-heading text-xl flex items-center gap-3">
        <div className="h-9 w-9 rounded bg-primary flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-primary-foreground" />
        </div>
        {title}
        {badge && <Badge className="bg-blue-100 text-blue-800 text-[10px]">{badge}</Badge>}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4 text-sm leading-relaxed">{children}</CardContent>
  </Card>
);

const Step = ({ number, title, children }) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center">
      <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
        {number}
      </div>
      <div className="w-px flex-1 bg-border mt-1" />
    </div>
    <div className="pb-6">
      <p className="font-semibold text-foreground mb-1">{title}</p>
      <div className="text-muted-foreground">{children}</div>
    </div>
  </div>
);

const Tip = ({ children }) => (
  <div className="flex gap-2 p-3 rounded bg-blue-50 border border-blue-200 text-blue-800 text-xs">
    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
    <div>{children}</div>
  </div>
);

export default function ManualPage() {
  return (
    <div className="space-y-8 animate-slide-in max-w-4xl" data-testid="manual-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Manual do Sistema</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Guia completo de utilizacao do sistema PCP Hidraulicos
          </p>
        </div>
      </div>

      {/* Index */}
      <Card className="border border-border shadow-none bg-muted/30">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Search className="h-4 w-4" /> Indice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm">
            {[
              { n: "1", t: "Primeiro Acesso e Login" },
              { n: "2", t: "Visao Geral (Dashboard)" },
              { n: "3", t: "Cadastro de Clientes" },
              { n: "4", t: "Controle de Estoque" },
              { n: "5", t: "Catalogo de Produtos" },
              { n: "6", t: "Ordens de Producao" },
              { n: "7", t: "Expedicao" },
              { n: "8", t: "Engenharia" },
              { n: "9", t: "Relatorios" },
              { n: "10", t: "Gerenciamento de Usuarios" },
              { n: "11", t: "Configuracoes" },
              { n: "12", t: "Fluxo Completo de Trabalho" },
            ].map((item) => (
              <div key={item.n} className="flex items-center gap-2 py-1">
                <span className="font-mono text-xs text-primary font-bold">{item.n}.</span>
                <span>{item.t}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 1. Login */}
      <Section icon={LogIn} title="1. Primeiro Acesso e Login">
        <p>O sistema exige autenticacao para acessar qualquer funcionalidade. Existem quatro perfis: <strong>Administrador</strong>, <strong>PCP</strong>, <strong>Operador</strong> e <strong>Engenharia</strong>.</p>
        <Step number="1" title="Acesse a pagina de login"><p>Ao abrir o sistema, voce sera redirecionado automaticamente para a tela de login.</p></Step>
        <Step number="2" title="Insira suas credenciais"><p>Digite seu <strong>usuario</strong> e <strong>senha</strong> nos campos indicados.</p></Step>
        <Step number="3" title='Clique em "Entrar"'><p>Se as credenciais estiverem corretas, voce sera levado ao Dashboard.</p></Step>
        <Tip>Credenciais iniciais do administrador: usuario <strong>admin</strong> / senha <strong>Admin@123</strong>. Recomendamos alterar a senha apos o primeiro acesso.</Tip>
        <Accordion type="single" collapsible>
          <AccordionItem value="perfis">
            <AccordionTrigger className="text-sm font-semibold">Diferenca entre perfis</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <div className="flex gap-2 items-start"><Badge className="bg-primary text-primary-foreground shrink-0">Admin</Badge><span>Acesso total: clientes, materiais, ordens, usuarios, produtos, expedicoes, engenharia, configuracoes e relatorios.</span></div>
              <div className="flex gap-2 items-start"><Badge className="bg-blue-100 text-blue-800 shrink-0">PCP</Badge><span>Acesso a todos os modulos operacionais (ordens, estoque, produtos, clientes, expedicao, engenharia, relatorios, dashboard). Nao gerencia usuarios nem configuracoes.</span></div>
              <div className="flex gap-2 items-start"><Badge className="bg-secondary text-secondary-foreground shrink-0">Operador</Badge><span>Foca na producao: visualiza ordens, atualiza status das etapas e empenha materiais. Acesso ao Manual.</span></div>
              <div className="flex gap-2 items-start"><Badge className="bg-amber-100 text-amber-800 shrink-0">Engenharia</Badge><span>Acesso restrito: Visao Geral, Produtos (criar/editar/copiar), Clientes (criar/editar), Engenharia (acesso total) e Manual.</span></div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Section>

      {/* 2. Dashboard */}
      <Section icon={LayoutDashboard} title="2. Visao Geral (Dashboard)">
        <p>O Dashboard apresenta um resumo visual de toda a operacao com indicadores atualizados em tempo real.</p>
        <h4 className="font-semibold text-foreground mt-2">Filtros</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Periodo</strong> - Hoje, Ultima Semana, Ultimo Mes ou Personalizado</li>
          <li><strong>Cliente</strong> - Filtre os dados por um cliente especifico</li>
          <li><strong>Prioridade</strong> - Filtre por Urgente, Alta, Media ou Baixa</li>
        </ul>
        <h4 className="font-semibold text-foreground mt-2">Indicadores (KPIs)</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
          <div className="p-2 rounded border border-border"><strong>Total de Ordens</strong> - Quantidade total no periodo</div>
          <div className="p-2 rounded border border-border"><strong>Em Andamento</strong> - Ordens com etapas iniciadas</div>
          <div className="p-2 rounded border border-border"><strong>Concluidas</strong> - Ordens finalizadas</div>
          <div className="p-2 rounded border border-border"><strong>Atrasadas</strong> - Ordens com prazo vencido</div>
          <div className="p-2 rounded border border-border"><strong>Clientes</strong> - Total cadastrados</div>
          <div className="p-2 rounded border border-border"><strong>Materiais</strong> - Total no estoque</div>
        </div>
        <h4 className="font-semibold text-foreground mt-2">Grafico de Gantt</h4>
        <p>O Dashboard inclui um <strong>grafico de Gantt</strong> que mostra as ordens como barras de tempo, com cada etapa representada por uma cor diferente. Use os controles de zoom (Dia/Semana/Mes) para ajustar a visualizacao. Ordens concluidas sao ocultadas automaticamente.</p>
      </Section>

      {/* 3. Clientes */}
      <Section icon={Users} title="3. Cadastro de Clientes">
        <p>Antes de criar ordens, cadastre os clientes. Acesse pelo menu <strong>"Clientes"</strong>.</p>
        <h4 className="font-semibold text-foreground">Cadastrar um novo cliente</h4>
        <Step number="1" title='Clique em "Novo Cliente"'><p>Abre o formulario de cadastro.</p></Step>
        <Step number="2" title="Preencha os dados"><p><strong>Nome</strong> (obrigatorio), <strong>Codigo</strong>, <strong>CNPJ</strong>, <strong>Contato</strong>, <strong>Endereco</strong>, <strong>Endereco de Entrega</strong>.</p></Step>
        <Step number="3" title="Preencha o CEP"><p>Ao digitar o <strong>CEP</strong>, o sistema preenche automaticamente <strong>Estado</strong> e <strong>Cidade</strong> via ViaCEP.</p></Step>
        <Step number="4" title='Clique em "Cadastrar"'><p>O cliente ficara disponivel para selecao nas ordens e expedicoes.</p></Step>
        <Tip>O codigo do cliente e importante para identificacao na expedicao. Clientes com mesmo nome mas codigos diferentes sao diferenciados pelo codigo.</Tip>
      </Section>

      {/* 4. Estoque */}
      <Section icon={Package} title="4. Controle de Estoque" badge="Atualizado">
        <p>Controle de todas as materias-primas, inclusive material <strong>consignado</strong> (enviado pelo cliente, que fica armazenado e e consumido na producao dos kits). Acesse pelo menu <strong>"Estoque"</strong>.</p>

        <h4 className="font-semibold text-foreground">Entendendo as colunas</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <div className="p-2 rounded border border-border"><strong>Qtd. Recebida</strong> - Total que ja chegou na fabrica. So aumenta com entradas/recebimentos.</div>
          <div className="p-2 rounded border border-border"><strong>Qtd. Utilizada</strong> - Total consumido na producao (empenho/baixa e saidas). Devolucao reduz.</div>
          <div className="p-2 rounded border border-border"><strong>Disponivel</strong> - Recebida menos Utilizada. E o saldo real para usar.</div>
          <div className="p-2 rounded border border-border"><strong>Qtd. Total</strong> - Total que o cliente deve enviar (consignado).</div>
          <div className="p-2 rounded border border-border"><strong>Falta Receber</strong> - Total menos Recebida. Quanto o cliente ainda precisa mandar.</div>
          <div className="p-2 rounded border border-border"><strong>Est. Min.</strong> - Quando o Disponivel fica abaixo, o material aparece em "Estoque Baixo".</div>
        </div>
        <Tip>O empenho de material na ordem <strong>NAO reduz</strong> a "Qtd. Recebida" - ele aumenta a "Qtd. Utilizada". Assim o controle de recebimento (o que o cliente mandou x o que falta) fica sempre correto.</Tip>

        <h4 className="font-semibold text-foreground">Cadastrar um material</h4>
        <p><strong>Nome</strong>, <strong>Codigo</strong>, <strong>Categoria</strong>, <strong>Unidade</strong>, <strong>Qtd. Recebida (inicial)</strong>, <strong>Qtd. Total</strong>, <strong>Estoque Minimo</strong>, <strong>Cliente</strong> (consignante) e <strong>Obra</strong>. No cadastro tambem ha o campo <strong>Numero da NF</strong> do recebimento inicial.</p>

        <h4 className="font-semibold text-foreground">Entrada / Saida</h4>
        <p>Use <ArrowDownCircle className="h-3 w-3 inline text-green-600" /> para <strong>entrada</strong> (recebimento - aumenta a Recebida) e <ArrowUpCircle className="h-3 w-3 inline text-orange-600 ml-1" /> para <strong>saida</strong> (consumo - aumenta a Utilizada). Na entrada voce pode informar o <strong>numero da NF</strong> e a <strong>obra</strong>.</p>

        <h4 className="font-semibold text-foreground">Historico</h4>
        <p>Clique em <History className="h-3 w-3 inline" /> para ver todas as movimentacoes, com NF, data da NF, obra e responsavel.</p>

        <Accordion type="single" collapsible>
          <AccordionItem value="importar-nf">
            <AccordionTrigger className="text-sm font-semibold"><span className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> Importar Nota Fiscal (entrada automatica)</span></AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>O botao <strong>"Importar NF"</strong> automatiza a entrada de materiais a partir da nota do fornecedor.</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Clique em <strong>Importar NF</strong> e envie o arquivo.</li>
                <li><strong>XML da NF-e</strong> - leitura exata e gratuita (padrao SEFAZ). <strong>Recomendado.</strong></li>
                <li><strong>PDF ou foto</strong> - leitura por <strong>IA</strong> (tem custo por leitura).</li>
                <li>O sistema mostra a <strong>tela de conferencia</strong>: numero e data da NF, fornecedor e itens.</li>
                <li>Selecione o <strong>cliente (consignante)</strong> dono do material.</li>
                <li>Para cada item: <strong>Criar novo</strong> material, <strong>Vincular</strong> a um existente, ou <strong>Ignorar</strong>.</li>
                <li>Clique em <strong>Confirmar Entrada</strong>. O estoque e atualizado e a NF fica registrada no historico.</li>
              </ol>
              <p>So a leitura inicial de PDF/foto tem custo. XML e sempre gratis. Conferir/confirmar nao geram custo.</p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Tip>Sempre que o fornecedor puder enviar o <strong>XML</strong> da NF-e, prefira o XML: e gratis e 100% exato.</Tip>
      </Section>

      {/* 5. Produtos */}
      <Section icon={BoxIcon} title="5. Catalogo de Produtos" badge="Novo">
        <p>O catalogo de produtos serve como <strong>template</strong> para criar ordens de producao rapidamente. Acesse pelo menu <strong>"Produtos"</strong>.</p>
        <h4 className="font-semibold text-foreground">Cadastrar um produto</h4>
        <Step number="1" title='Clique em "Novo Produto"'><p>Abre o formulario de cadastro.</p></Step>
        <Step number="2" title="Preencha os dados do produto">
          <p>
            <strong>Codigo</strong> (ex: 95100000000), <strong>Nome</strong>, <strong>Numero do Desenho</strong>,
            <strong> SKU / Ordem de Fabricacao</strong>, <strong>Preco de Venda</strong>.
          </p>
        </Step>
        <Step number="3" title="Preencha as dimensoes e cubagem">
          <p><strong>Peso (kg)</strong>, <strong>Altura (m)</strong>, <strong>Largura (m)</strong>, <strong>Comprimento (m)</strong>. O volume (m3) e calculado automaticamente.</p>
        </Step>
        <Step number="4" title="Adicione etapas e sub-etapas">
          <p>Defina as etapas do processo produtivo com sub-etapas e materiais. Esses dados serao copiados automaticamente ao criar uma ordem baseada nesse produto.</p>
        </Step>
        <Step number="5" title='Clique em "Salvar"'><p>O produto fica disponivel como template nas ordens.</p></Step>
        <h4 className="font-semibold text-foreground">Duplicar produto</h4>
        <p>Abra o detalhe de um produto e clique em <strong>"Duplicar"</strong> para criar uma copia. Ideal para produtos similares com pequenas variacoes.</p>
        <h4 className="font-semibold text-foreground">Desenhos Tecnicos</h4>
        <p>No detalhe do produto (icone <Eye className="h-3 w-3 inline" />) ha a secao <strong>"Desenhos Tecnicos"</strong>. Anexe o desenho (imagem ou PDF) uma unica vez no cadastro do produto. <strong>Toda ordem criada a partir desse produto imprime o desenho automaticamente</strong> - sem precisar anexar em cada ordem.</p>
        <Tip>Ao criar uma ordem usando um produto como template, as quantidades das sub-etapas e materiais sao automaticamente multiplicadas pela quantidade da ordem. O produto tambem fica vinculado a ordem, o que permite puxar dados automaticamente na expedicao (desenho, preco, cubagem) e imprimir os desenhos tecnicos junto.</Tip>
      </Section>

      {/* 6. Ordens de Producao */}
      <Section icon={ClipboardList} title="6. Ordens de Producao" badge="Modulo Principal">
        <p>Modulo central do sistema. Crie e acompanhe cada pedido de producao. Acesse pelo menu <strong>"Ordens de Producao"</strong>.</p>
        <h4 className="font-semibold text-foreground">Criar uma nova ordem</h4>
        <Step number="1" title='Clique em "Nova Ordem"'><p>Abre o formulario de criacao.</p></Step>
        <Step number="2" title="Selecione um produto (opcional)"><p>Escolha um produto do catalogo para preencher automaticamente as etapas, sub-etapas e materiais. As quantidades sao multiplicadas pela quantidade da ordem.</p></Step>
        <Step number="3" title="Preencha os dados"><p><strong>Cliente</strong>, <strong>Titulo</strong>, <strong>Codigo da Ordem</strong>, <strong>Quantidade</strong>, <strong>Data de Inicio</strong>, <strong>Prazo de Entrega</strong>, <strong>Prioridade</strong>.</p></Step>
        <Step number="4" title="Configure as etapas">
          <p>Cada etapa tem campos de <strong>Data de Inicio</strong> e <strong>Prazo de Entrega</strong>. Ao preencher as datas na etapa, elas <strong>propagam automaticamente</strong> para todas as sub-etapas. Voce pode ajustar datas individuais de cada sub-etapa manualmente se necessario.</p>
        </Step>
        <Step number="5" title='Clique em "Criar Ordem"'><p>A ordem sera criada com status "Pendente".</p></Step>

        <Accordion type="single" collapsible>
          <AccordionItem value="etapas">
            <AccordionTrigger className="text-sm font-semibold">Gerenciar etapas e sub-etapas</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Cada etapa e sub-etapa tem um seletor de status:</p>
              <div className="flex flex-wrap gap-2 mt-1">
                <Badge className="bg-slate-200 text-slate-700">Pendente</Badge>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <Badge className="bg-blue-200 text-blue-800">Em Andamento</Badge>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <Badge className="bg-green-200 text-green-800">Concluido</Badge>
              </div>
              <p>As etapas <strong>nao sao sequenciais</strong> - voce pode trabalhar em qualquer etapa a qualquer momento.</p>
              <p>Se uma etapa possui sub-etapas, o status da etapa e calculado automaticamente.</p>
              <p><strong>Datas nas etapas:</strong> Ao alterar datas de inicio ou prazo de uma etapa, todas as sub-etapas sao atualizadas automaticamente.</p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="materiais">
            <AccordionTrigger className="text-sm font-semibold">Empenho de materiais por etapa</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Cada etapa possui sua propria lista de materiais empenhados.</p>
              <p className="font-semibold">Adicionar material:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Expanda a etapa e use o campo de busca por <strong>codigo</strong> ou <strong>nome</strong></li>
                <li>Informe a <strong>quantidade planejada</strong> e clique em <Plus className="h-3 w-3 inline" /></li>
              </ul>
              <p className="font-semibold mt-2">Confirmar baixa:</p>
              <p>Clique em <CheckCircle2 className="h-3 w-3 inline text-green-600" /> para confirmar entrega. O estoque atualiza automaticamente.</p>
              <p className="font-semibold mt-2">Devolver ao estoque:</p>
              <p>Clique em <RotateCcw className="h-3 w-3 inline text-orange-500" /> para devolver materiais confirmados.</p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="impressao">
            <AccordionTrigger className="text-sm font-semibold">Imprimir ordem de producao</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Clique em <Printer className="h-3 w-3 inline" /> <strong>Imprimir</strong> para gerar a folha de producao.</p>
              <p>O layout de impressao inclui:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li><strong>Cabecalho</strong> - titulo, codigo, cliente, quantidade, prioridade, datas e status</li>
                <li><strong>Materiais Empenhados</strong> - tabela consolidada de todas as etapas</li>
                <li><strong>Etapas do Processo</strong> - lista com sub-etapas, datas e status</li>
                <li><strong>Campos de Assinatura</strong> - dois quadros para aprovacao:
                  <ul className="list-disc pl-5 mt-1">
                    <li><strong>Qualidade</strong> - Data, hora e assinatura</li>
                    <li><strong>Expedicao</strong> - Data, hora, peso, cubagem, volume e assinatura</li>
                  </ul>
                </li>
                <li><strong>Desenhos Tecnicos</strong> - desenhos anexados ao produto vinculado e/ou a propria ordem, cada um em sua propria pagina</li>
              </ol>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="vincular">
            <AccordionTrigger className="text-sm font-semibold">Vincular produto a uma ordem existente</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Para ordens criadas sem template, voce pode vincular um produto manualmente:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Abra a ordem e clique em <Pencil className="h-3 w-3 inline" /> <strong>Editar</strong></li>
                <li>No campo <strong>"Produto Vinculado"</strong>, selecione o produto do catalogo</li>
                <li>Salve a ordem</li>
              </ul>
              <p>Isso permite que a expedicao puxe automaticamente os dados do produto (desenho, preco, cubagem).</p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Tip>Use o botao <strong>Duplicar</strong> para criar ordens similares rapidamente. Os dados da ordem sao copiados, mas sem datas preenchidas para voce definir novos prazos.</Tip>
      </Section>

      {/* 7. Expedicao */}
      <Section icon={Truck} title="7. Expedicao" badge="Novo">
        <p>O modulo de expedicao controla o envio dos produtos finalizados ao cliente. Acesse pelo menu <strong>"Expedicao"</strong>.</p>
        <h4 className="font-semibold text-foreground">Conceito</h4>
        <p>Quando uma ordem de producao e <strong>concluida</strong> (todas as etapas finalizadas), a quantidade produzida fica disponivel para expedicao. Voce pode expedir quantidades parciais (ex: de 24 unidades, enviar 10 agora e 14 depois).</p>

        <h4 className="font-semibold text-foreground">Criar uma expedicao</h4>
        <Step number="1" title='Clique em "Nova Expedicao"'><p>Abre a tela de criacao em pagina inteira com duas colunas.</p></Step>
        <Step number="2" title="Selecione o cliente"><p>No painel esquerdo, escolha o cliente. O sistema mostrara as <strong>ordens finalizadas</strong> desse cliente com a quantidade disponivel.</p></Step>
        <Step number="3" title="Selecione a empresa emissora (opcional)"><p>Se houver empresas cadastradas em Configuracoes, selecione qual empresa emite esta expedicao.</p></Step>
        <Step number="4" title="Adicione itens">
          <p>Tres formas de adicionar itens:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Ordens finalizadas</strong> - Clique na ordem no painel esquerdo. Os dados do produto vinculado (desenho, preco, peso, cubagem) sao preenchidos automaticamente</li>
            <li><strong>Produto</strong> - Clique em "Produto" para buscar no catalogo</li>
            <li><strong>Manual</strong> - Clique em "Manual" para adicionar um item em branco</li>
          </ul>
        </Step>
        <Step number="5" title="Preencha os volumes">
          <p>Para cada item, informe o numero de <strong>volumes</strong> (caixas/embalagens). O total de volumes e somado automaticamente.</p>
        </Step>
        <Step number="6" title="Ajuste quantidades"><p>Se necessario, altere a quantidade de cada item (ex: de 24, enviar apenas 10). O peso e recalculado.</p></Step>
        <Step number="7" title="Preencha transporte (opcional)"><p><strong>Transportadora</strong>, <strong>Placa</strong>, <strong>Motorista</strong> e observacoes.</p></Step>
        <Step number="8" title='Clique em "Criar Expedicao"'><p>A expedicao recebe um codigo sequencial (EXP-001, EXP-002...) e as quantidades expedidas sao descontadas das ordens.</p></Step>

        <h4 className="font-semibold text-foreground">Status da expedicao</h4>
        <div className="flex flex-wrap gap-2 mt-1">
          <Badge className="bg-yellow-100 text-yellow-800">Preparando</Badge>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <Badge className="bg-blue-100 text-blue-800">Pronto p/ Envio</Badge>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <Badge className="bg-green-100 text-green-800">Expedido</Badge>
        </div>

        <Accordion type="single" collapsible>
          <AccordionItem value="detalhe">
            <AccordionTrigger className="text-sm font-semibold">Detalhe da expedicao</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Clique em uma expedicao na lista para ver os detalhes. A tela mostra:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Resumo</strong> - Total de itens, volumes, peso, cubagem e valor total (formato brasileiro: R$ xxx.xxx,xx)</li>
                <li><strong>Transporte</strong> - Transportadora, placa e motorista</li>
                <li><strong>Itens</strong> - Tabela com produto, desenho, ordem fabricacao, volumes, quantidade, preco e cubagem</li>
                <li><strong>Materiais Utilizados</strong> - Lista consolidada de todos os materiais das ordens de producao vinculadas, com quantidade prevista e confirmada</li>
              </ul>
              <p>Botoes disponiveis: <Printer className="h-3 w-3 inline" /> <strong>Imprimir</strong>, <Pencil className="h-3 w-3 inline" /> <strong>Editar</strong>, <RefreshCw className="h-3 w-3 inline" /> <strong>Recalcular</strong> (corrige pesos e dados), <Trash2 className="h-3 w-3 inline text-destructive" /> <strong>Excluir</strong> (devolve quantidades as ordens).</p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="impressao-exp">
            <AccordionTrigger className="text-sm font-semibold">Imprimir romaneio</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>O documento impresso inclui:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li><strong>Cabecalho</strong> - empresa emissora, codigo da expedicao e data</li>
                <li><strong>Resumo</strong> - cliente (com codigo), itens, volumes, peso total e <strong>valor total</strong></li>
                <li><strong>Transporte</strong> - transportadora, placa e motorista</li>
                <li><strong>Tabela de itens</strong> - produto, desenho, ordem fabricacao, volumes, quantidade, preco unitario, subtotal, peso unitario e cubagem</li>
                <li><strong>Materiais utilizados</strong> - tabela com codigo, material, unidade, quantidade prevista e confirmada</li>
              </ol>
              <p>Todos os valores monetarios e de peso sao formatados no padrao brasileiro (R$ xxx.xxx,xx / x.xxx,xx kg).</p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Tip>Use o botao <strong>Recalcular</strong> em expedicoes antigas para corrigir pesos e preencher dados de produtos que foram vinculados posteriormente.</Tip>
      </Section>

      {/* 8. Engenharia */}
      <Section icon={HardHat} title="8. Engenharia" badge="Novo">
        <p>Acompanhamento dos projetos de engenharia em formato de quadro, com as etapas: <strong>Cotacao</strong>, <strong>Codigo</strong>, <strong>Lista de Material</strong>, <strong>Desenho</strong> e <strong>Liberacao de Material</strong>. Acesse pelo menu <strong>"Engenharia"</strong>.</p>
        <h4 className="font-semibold text-foreground">Criar e gerenciar projetos</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li>Cada projeto tem <strong>Cliente</strong> e <strong>Obra</strong> (um mesmo cliente pode ter varias obras).</li>
          <li>Cada etapa tem um <strong>status</strong> (Feito, Produzindo, Nao liberado, Liberado) e data.</li>
          <li>O <strong>responsavel</strong> e preenchido automaticamente com o usuario que fez a alteracao.</li>
          <li><strong>Anexos</strong>: e possivel anexar arquivos ao projeto.</li>
          <li><strong>Historico de alteracoes</strong>: o botao <History className="h-3 w-3 inline" /> mostra quem alterou, o que mudou e quando.</li>
          <li>Use os filtros por <strong>obra</strong> e por <strong>status</strong> para localizar projetos.</li>
        </ul>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
          <div className="p-2 rounded border-t-4 border-t-green-500 bg-muted/30"><strong>Feito</strong></div>
          <div className="p-2 rounded border-t-4 border-t-blue-500 bg-muted/30"><strong>Produzindo</strong></div>
          <div className="p-2 rounded border-t-4 border-t-red-500 bg-muted/30"><strong>Nao liberado</strong></div>
          <div className="p-2 rounded border-t-4 border-t-emerald-600 bg-muted/30"><strong>Liberado</strong></div>
        </div>
        <Tip>Existe um perfil de usuario dedicado a <strong>Engenharia</strong>, com acesso restrito a este modulo, Produtos, Clientes, Visao Geral e Manual (veja a secao Usuarios).</Tip>
      </Section>

      {/* 9. Relatorios */}
      <Section icon={BarChart3} title="9. Relatorios">
        <p>Analises da producao em diferentes periodos. Acesse pelo menu <strong>"Relatorios"</strong>.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Total de Ordens</strong> criadas no periodo</li>
          <li><strong>Total de Movimentacoes</strong> de estoque</li>
          <li><strong>Entradas vs Saidas</strong> de materiais</li>
          <li><strong>Grafico pizza</strong> - Distribuicao por status</li>
          <li><strong>Grafico barras</strong> - Ordens por cliente (top 8)</li>
        </ul>
      </Section>

      {/* 10. Usuarios */}
      <Section icon={UserCog} title="10. Gerenciamento de Usuarios" badge="Somente Admin">
        <p>Apenas administradores podem acessar. Acesse pelo menu <strong>"Usuarios"</strong>.</p>
        <p>Crie usuarios com <strong>Nome</strong>, <strong>Usuario</strong>, <strong>Email</strong>, <strong>Senha</strong> e <strong>Perfil</strong> (Admin, PCP, Operador ou Engenharia). Administradores podem editar dados e redefinir senhas de outros usuarios.</p>
        <Tip>Crie contas de <strong>Operador</strong> para a producao (atualizam status e empenham materiais), <strong>Engenharia</strong> para a equipe de projetos e <strong>PCP</strong> para o planejamento. Cada perfil ve apenas os modulos pertinentes.</Tip>
      </Section>

      {/* 11. Configuracoes */}
      <Section icon={Settings} title="11. Configuracoes" badge="Somente Admin">
        <p>Personalize os parametros do sistema. Acesse pelo menu <strong>"Configuracoes"</strong>.</p>
        <h4 className="font-semibold text-foreground">Categorias de Materiais</h4>
        <p>Defina as categorias disponiveis no cadastro de materiais (ex: Tubo, Conexao, Perfil).</p>
        <h4 className="font-semibold text-foreground">Unidades de Medida</h4>
        <p>Defina as unidades (ex: Metro, Unidade, Kg, Rolo, Peca, Litro).</p>
        <h4 className="font-semibold text-foreground">Etapas Padrao</h4>
        <p>Defina as etapas que aparecem no dropdown ao criar ordens (ex: Corte de Tubo, Solda, Montagem, Teste de Pressao).</p>
        <h4 className="font-semibold text-foreground">Empresas</h4>
        <p>Cadastre as empresas emissoras das ordens de expedicao. Cada empresa possui: <strong>Razao Social</strong>, <strong>CNPJ</strong>, <strong>Endereco</strong> (CEP, cidade, estado), <strong>Telefone</strong> e <strong>Email</strong>. Se voce possui mais de uma empresa, cadastre todas aqui para selecionar na hora de criar a expedicao.</p>
        <Tip>Configure as etapas padrao de acordo com o fluxo da sua fabrica. Cadastre todas as suas empresas para que os romaneios saiam com os dados corretos.</Tip>
      </Section>

      {/* 12. Fluxo Completo */}
      <Section icon={ChevronRight} title="12. Fluxo Completo de Trabalho">
        <p className="font-semibold">Fluxo recomendado para utilizar o sistema do inicio ao fim:</p>
        <div className="mt-4 space-y-0">
          <Step number="1" title="Configure o sistema"><p>Configuracoes: categorias, unidades, etapas padrao e empresas.</p></Step>
          <Step number="2" title="Cadastre clientes"><p>Clientes com codigo, nome, CNPJ e endereco (CEP auto-preenche).</p></Step>
          <Step number="3" title="Cadastre materiais"><p>Estoque: materias-primas com codigo, categoria e estoque minimo.</p></Step>
          <Step number="4" title="Cadastre produtos"><p>Produtos: templates com etapas, sub-etapas, materiais, preco e cubagem.</p></Step>
          <Step number="5" title="Registre entradas de material"><p>Ao receber materias-primas, registre a entrada no estoque (com NF e obra) ou use <strong>Importar NF</strong> para dar entrada automatica a partir do XML/PDF da nota.</p></Step>
          <Step number="6" title="Crie a ordem de producao"><p>Selecione produto template, cliente, quantidade e prazos. As etapas e materiais sao preenchidos automaticamente.</p></Step>
          <Step number="7" title="Configure datas nas etapas"><p>Defina inicio e prazo em cada etapa - as datas propagam para sub-etapas.</p></Step>
          <Step number="8" title="Imprima a ordem"><p>Folha de producao com materiais, etapas e campos de assinatura (qualidade e expedicao).</p></Step>
          <Step number="9" title="Confirme materiais"><p>A cada etapa concluida, confirme a baixa dos materiais. O estoque atualiza automaticamente.</p></Step>
          <Step number="10" title="Crie a expedicao"><p>Quando a ordem finalizar, crie a expedicao selecionando o cliente, ordens e quantidades a enviar.</p></Step>
          <Step number="11" title="Imprima o romaneio"><p>Documento com itens, volumes, materiais utilizados e valor total para acompanhar a carga.</p></Step>
        </div>
        <Tip>Mantenha o sistema atualizado! Oriente sua equipe a atualizar status e confirmar materiais em tempo real. Quanto mais preciso, melhores as decisoes.</Tip>
      </Section>
    </div>
  );
}
